import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, X, Sparkles, ChevronDown } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

export interface EligibleLine {
  product_id: number;
  original_unit_price: number;
  new_unit_price: number;
  quantity_affected: number;
  discount_per_unit: number;
}

export interface EligiblePromotion {
  promotion_id: number;
  name: string;
  description?: string;
  promotion_type: string;
  savings: number;
  lines: EligibleLine[];
}

interface PromotionBannerProps {
  storeId: number;
  // The basket reduced to what the API needs
  items: Array<{ product_id: number; quantity: number; unit_price: number }>;
  // Currently applied promotion id, if any
  appliedPromotionId: number | null;
  // Called when the user clicks a promotion to apply it
  onApply: (promo: EligiblePromotion) => void;
  // Called when the user removes the currently applied promotion
  onRemove: () => void;
}

const PromotionBanner: React.FC<PromotionBannerProps> = ({
  storeId, items, appliedPromotionId, onApply, onRemove
}) => {
  const { t } = useTranslation(['common']);
  const [eligible, setEligible] = useState<EligiblePromotion[]>([]);
  const [expanded, setExpanded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchEligible = async () => {
    if (!storeId || items.length === 0) {
      setEligible([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/promotions/eligible`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({ items })
      });
      if (res.ok) {
        const data = await res.json();
        setEligible(data.eligible || []);
      }
    } catch (e) {
      // silent — banner just won't show
    }
  };

  // Re-evaluate when basket changes (debounced) and every 60s for time-based promos
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchEligible, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items), storeId]);

  useEffect(() => {
    const id = setInterval(fetchEligible, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, JSON.stringify(items)]);

  // Hide if applied promo is no longer in the eligible list (basket changed it out of validity)
  useEffect(() => {
    if (appliedPromotionId && !eligible.some(p => p.promotion_id === appliedPromotionId)) {
      onRemove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, appliedPromotionId]);

  if (eligible.length === 0 && !appliedPromotionId) return null;

  const applied = eligible.find(p => p.promotion_id === appliedPromotionId);
  const others = eligible.filter(p => p.promotion_id !== appliedPromotionId);

  return (
    <div className="mb-3">
      {/* Applied banner */}
      {applied && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-500/15 border border-emerald-500/40 rounded-xl mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-emerald-800 dark:text-emerald-200 truncate">
                {t('common:promotionApplied', { defaultValue: 'Applied' })}: {applied.name}
              </div>
              <div className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
                {t('common:promotionSavings', { defaultValue: 'You save' })} -{applied.savings.toFixed(2)}€
              </div>
            </div>
          </div>
          <button
            onClick={onRemove}
            className="p-1.5 rounded-lg hover:bg-emerald-500/20 text-emerald-800 dark:text-emerald-200"
            title={t('common:promotionRemove', { defaultValue: 'Remove promotion' })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Available promos */}
      {others.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-xl">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between px-3 py-2 text-left"
          >
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-amber-700 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                {others.length === 1
                  ? `${t('common:promotionAvailable', { defaultValue: 'Available' })}: ${others[0].name} (-${others[0].savings.toFixed(2)}€)`
                  : t('common:promotionAvailableCount', { count: others.length, defaultValue: '{{count}} available promotions' })}
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-amber-800 dark:text-amber-300 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          {expanded && (
            <div className="px-3 pb-2 space-y-1.5">
              {others.map(p => (
                <button
                  key={p.promotion_id}
                  onClick={() => { onApply(p); setExpanded(false); }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-surface-1 hover:bg-surface-2 rounded-lg text-left transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-t1 truncate">{p.name}</div>
                    {p.description && <div className="text-xs text-t3 truncate">{p.description}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-bold text-amber-800 dark:text-amber-300">-{p.savings.toFixed(2)}€</span>
                    <span className="px-2 py-0.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded text-xs font-semibold">
                      {t('common:apply', { defaultValue: 'Apply' })}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromotionBanner;
